import {
  Mail,
  MailCheck,
  MailMinus,
  MailWarning,
  type LucideIcon,
} from "lucide-react";

/**
 * Estado de COMUNICACIONES por email de un `Customer`, fuente de verdad en el
 * backend: `customers/email-status.ts` (que a su vez reutiliza
 * `getIneligibleReason` de `email-campaigns/eligibility.ts`, evaluado en modo
 * "soft"). Este fichero SOLO pinta el mismo contrato en el dashboard — no
 * reimplementa ningún criterio de elegibilidad.
 *
 * "alta" = recibiría campañas en modo soft (email utilizable, sin baja, sin
 * rebote). NO implica consentimiento explícito de marketing.
 */
export type CustomerEmailStatus = "alta" | "baja" | "rebotado" | "sin_email";

export interface EmailStatusCounts {
  total: number;
  alta: number;
  baja: number;
  rebotado: number;
  sin_email: number;
}

interface EmailStatusVisual {
  label: string;
  badgeClassName: string;
  icon: LucideIcon;
}

export const EMAIL_STATUS_CONFIG: Record<CustomerEmailStatus, EmailStatusVisual> = {
  alta: {
    label: "Alta",
    badgeClassName: "bg-green-100 text-green-700",
    icon: MailCheck,
  },
  baja: {
    label: "De baja",
    badgeClassName: "bg-amber-100 text-amber-700",
    icon: MailMinus,
  },
  rebotado: {
    label: "Rebotado",
    badgeClassName: "bg-orange-100 text-orange-700",
    icon: MailWarning,
  },
  sin_email: {
    label: "Sin email",
    badgeClassName: "bg-gray-100 text-gray-500",
    icon: Mail,
  },
};

/**
 * Devuelve la config visual de un estado, o null si el estado es desconocido.
 *
 * Sin fallback a "alta": si el backend aún no devuelve `emailStatus` (ventana
 * de despliegue dashboard/API) pintarlo todo como "Alta" en verde etiquetaría
 * como suscritos a clientes de baja o rebotados. Mejor no pintar badge.
 */
export function getEmailStatusConfig(
  status: CustomerEmailStatus | undefined,
): EmailStatusVisual | null {
  return status ? EMAIL_STATUS_CONFIG[status] : null;
}

/** Opciones del filtro rápido de la barra de Clientes (incluye "Todos" = sin filtro). */
export const EMAIL_STATUS_FILTER_OPTIONS: {
  value: CustomerEmailStatus | "";
  label: string;
}[] = [
  { value: "", label: "Todos" },
  { value: "alta", label: "Alta" },
  { value: "baja", label: "De baja" },
  { value: "rebotado", label: "Rebotado" },
  { value: "sin_email", label: "Sin email" },
];

/**
 * Motivos de baja legibles en español. `optOutReason` es un campo compartido
 * en `Customer` entre la baja de marketing y la baja transaccional (WhatsApp
 * transaccional / email transaccional) — ver `opt-out-service.ts` en backend.
 */
const OPT_OUT_REASON_LABELS: Record<string, string> = {
  manual: "Baja manual desde el panel",
  email_link: "Enlace de baja del email",
  email_link_oneclick: "Enlace de baja del email (un clic)",
  whatsapp_reply: "Respuesta al chatbot de WhatsApp",
  stop_keyword: "Palabra clave de baja por WhatsApp",
};

/** Traduce `optOutReason` a un texto legible; devuelve el valor crudo si no se reconoce. */
export function optOutReasonLabel(reason: string | null | undefined): string {
  if (!reason) return "Sin motivo registrado";
  return OPT_OUT_REASON_LABELS[reason] ?? reason;
}

/**
 * Motivos que indican que la baja la pidió el propio cliente (RGPD Art. 21):
 * el negocio no puede reactivarlo desde el panel, solo el interesado desde su
 * enlace de preferencias. Debe coincidir 1:1 con
 * `CUSTOMER_INITIATED_OPT_OUT_REASONS` en `customers/controller.ts` (backend).
 * Se usa solo para dar contexto en la UI antes de intentar la reactivación —
 * el backend es quien impone el 409 real.
 */
const CUSTOMER_INITIATED_OPT_OUT_REASONS = new Set<string>([
  "email_link",
  "email_link_oneclick",
  "whatsapp_reply",
  "stop_keyword",
]);

/** true si, por el motivo registrado, reactivar desde el panel devolvería 409. */
export function isCustomerInitiatedOptOut(
  reason: string | null | undefined,
): boolean {
  return !!reason && CUSTOMER_INITIATED_OPT_OUT_REASONS.has(reason);
}
